import type { BaseUser, Device } from "@models";
import type { DocStatus, FunctionalGroup, RegulatoryTypeEnum } from "@types";
import { useMemo, useState } from "react";
import {
  EMPTY_DOCUMENTS_FILTERS,
  getDocumentTypeFilterOptions,
  getFunctionalGroupFilterOptions,
  getSelectedDocumentFilterUsers,
  getSortedDeviceFilterOptions,
  getStatusFilterOptions,
  hasActiveDocumentsFilters,
  toggleDocumentsFilterSelection,
  type DocumentsActionFilterValue,
  type DocumentsFilterState,
  type FilterOption,
} from "./Documents.filtering";

type DocumentsFilterOptions = {
  approverUsers: BaseUser[];
  authorUsers: BaseUser[];
  deviceOptions: FilterOption[];
  documentTypeOptions: FilterOption<RegulatoryTypeEnum>[];
  functionalGroupOptions: FilterOption<string>[];
  statusOptions: FilterOption<DocStatus>[];
};

type DocumentsFilterSelectedUsers = {
  approvers: BaseUser[];
  authors: BaseUser[];
};

type DocumentsFilterActions = {
  resetFilters: () => void;
  setSelectedApproverUsers: (selectedUsers: BaseUser[]) => void;
  setSelectedAuthorUsers: (selectedUsers: BaseUser[]) => void;
  toggleActionFilter: (value: DocumentsActionFilterValue) => void;
  toggleDevice: (deviceId: string) => void;
  toggleDocumentType: (documentType: RegulatoryTypeEnum) => void;
  toggleFunctionalGroup: (functionalGroupId: string) => void;
  toggleOrgOnly: () => void;
  toggleStatus: (status: DocStatus) => void;
};

type UseDocumentsFiltersParams = {
  devices: Device[];
  functionalGroups: FunctionalGroup[] | undefined;
  users: BaseUser[];
};

type DocumentsArrayFilterKey =
  | "selectedActionFilters"
  | "selectedDeviceIds"
  | "selectedDocumentTypes"
  | "selectedFunctionalGroupIds"
  | "selectedStatuses";

export type UseDocumentsFiltersResult = {
  actions: DocumentsFilterActions;
  filters: DocumentsFilterState;
  hasActiveFilters: boolean;
  options: DocumentsFilterOptions;
  selectedUsers: DocumentsFilterSelectedUsers;
};

export const useDocumentsFilters = ({
  devices,
  functionalGroups,
  users,
}: UseDocumentsFiltersParams): UseDocumentsFiltersResult => {
  const [filters, setFilters] = useState<DocumentsFilterState>(
    EMPTY_DOCUMENTS_FILTERS
  );

  const toggleFilterValue = <TValue extends string>(
    key: DocumentsArrayFilterKey,
    value: TValue
  ) => {
    setFilters((current) => ({
      ...current,
      [key]: toggleDocumentsFilterSelection(current[key] as TValue[], value),
    }));
  };

  const setSelectedUsers = (
    key: "selectedApproverUserIds" | "selectedAuthorUserIds",
    selectedUsers: BaseUser[]
  ) => {
    setFilters((current) => ({
      ...current,
      [key]: selectedUsers.map((user) => user.id),
    }));
  };

  const options = useMemo(
    () => ({
      approverUsers: users,
      authorUsers: users,
      deviceOptions: getSortedDeviceFilterOptions(devices),
      documentTypeOptions: getDocumentTypeFilterOptions(),
      functionalGroupOptions: getFunctionalGroupFilterOptions(functionalGroups),
      statusOptions: getStatusFilterOptions(),
    }),
    [devices, functionalGroups, users]
  );

  const selectedUsers = useMemo(
    () => ({
      approvers: getSelectedDocumentFilterUsers(
        filters.selectedApproverUserIds,
        users
      ),
      authors: getSelectedDocumentFilterUsers(
        filters.selectedAuthorUserIds,
        users
      ),
    }),
    [filters.selectedApproverUserIds, filters.selectedAuthorUserIds, users]
  );

  return {
    actions: {
      resetFilters: () => setFilters(EMPTY_DOCUMENTS_FILTERS),
      setSelectedApproverUsers: (users) =>
        setSelectedUsers("selectedApproverUserIds", users),
      setSelectedAuthorUsers: (users) =>
        setSelectedUsers("selectedAuthorUserIds", users),
      toggleActionFilter: (value) =>
        toggleFilterValue("selectedActionFilters", value),
      toggleDevice: (value) => toggleFilterValue("selectedDeviceIds", value),
      toggleDocumentType: (value) =>
        toggleFilterValue("selectedDocumentTypes", value),
      toggleFunctionalGroup: (value) =>
        toggleFilterValue("selectedFunctionalGroupIds", value),
      toggleOrgOnly: () =>
        setFilters((current) => ({
          ...current,
          isOrgOnlySelected: !current.isOrgOnlySelected,
        })),
      toggleStatus: (value) => toggleFilterValue("selectedStatuses", value),
    },
    filters,
    hasActiveFilters: hasActiveDocumentsFilters(filters),
    options,
    selectedUsers,
  };
};
